import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import Map, { AttributionControl, NavigationControl } from '@vis.gl/react-maplibre';
import type { MapEvent, MapProps, MapRef } from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { t, useI18n } from '../../i18n';
import { useMapConfig } from '../../hooks/useMapConfig';
import { BASEMAP_STYLE_URL, prepareMapLibre } from '../../utils/maplibre';
import type { MapLibreLibrary } from '../../utils/maplibre';

const DEFAULT_STYLE: React.CSSProperties = { width: '100%', height: '100%' };
const RETRY_COOLDOWN_MS = 5 * 60 * 1000;

interface Props extends Omit<MapProps, 'mapLib' | 'mapStyle' | 'style'> {
  style?: React.CSSProperties;
  showNavigation?: boolean;
  navigationPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  loadingFallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
}

const MapLibreMap = forwardRef<MapRef, Props>(function MapLibreMap(
  {
    children,
    style,
    showNavigation = true,
    navigationPosition = 'top-right',
    loadingFallback = null,
    errorFallback = null,
    onLoad,
    onError,
    ...props
  },
  ref,
) {
  useI18n();
  const { config, isLoading, error, refresh } = useMapConfig();
  const [mapLib, setMapLib] = useState<MapLibreLibrary | null>(null);
  const [mapLibError, setMapLibError] = useState<Error | null>(null);
  const lastRetryRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    if (!config) {
      setMapLib(null);
      return undefined;
    }
    prepareMapLibre(config.archives)
      .then((library) => {
        if (cancelled) return;
        setMapLib(library);
        setMapLibError(null);
      })
      .catch((loadError: Error) => {
        if (!cancelled) setMapLibError(loadError);
      });
    return () => { cancelled = true; };
  }, [config]);

  const handleLoad = useCallback((event: MapEvent) => {
    const map = event.target;
    map.touchZoomRotate.disableRotation();
    map.dragRotate.disable();
    map.keyboard.disableRotation();
    map.setPitch(0);
    map.setBearing(0);

    // MapLibre opens compact attribution until the first drag. Start it
    // collapsed so the map stays uncluttered while credits remain one click away.
    const attribution = map.getContainer().querySelector<HTMLDetailsElement>(
      '.maplibregl-ctrl-attrib.maplibregl-compact',
    );
    attribution?.classList.remove('maplibregl-compact-show');
    attribution?.removeAttribute('open');

    onLoad?.(event);
  }, [onLoad]);

  const handleError = useCallback((event: Parameters<NonNullable<MapProps['onError']>>[0]) => {
    const now = Date.now();
    if (now - lastRetryRef.current > RETRY_COOLDOWN_MS) {
      lastRetryRef.current = now;
      void refresh(true);
    }
    onError?.(event);
  }, [onError, refresh]);

  if (isLoading || (config && !mapLib && !mapLibError)) {
    return (loadingFallback as React.ReactElement) ?? (
      <div className="maplibre-shell maplibre-shell--loading" aria-busy="true" aria-label={t('Loading map')}>
        <div className="spinner spinner--sm" />
      </div>
    );
  }

  if (!config || error || mapLibError || !mapLib) {
    return (errorFallback as React.ReactElement) ?? (
      <div className="maplibre-shell maplibre-shell--error" role="status">
        <p className="maplibre-shell__message">{t('Map unavailable')}</p>
      </div>
    );
  }

  return (
    <Map
      ref={ref}
      mapLib={mapLib}
      mapStyle={BASEMAP_STYLE_URL}
      reuseMaps
      dragRotate={false}
      pitchWithRotate={false}
      maxPitch={0}
      attributionControl={false}
      style={{ ...DEFAULT_STYLE, ...style }}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    >
      {children}
      {showNavigation && (
        <NavigationControl position={navigationPosition} showCompass={false} visualizePitch={false} />
      )}
      <AttributionControl position="bottom-right" compact />
    </Map>
  );
});

export default MapLibreMap;
